import { DEFAULT_TIP_PERCENT, TIP_PERCENTS, formatMoney, formatPercent } from '../lib/money'
import type { AutoGratEntryMode } from '../lib/money'
import { VENUE_KINDS, type VenueKind } from '../lib/types'
import { MoneyInput } from './MoneyInput'

type Props = {
  billInput: string
  tipInput: string
  tipPercent: number
  presetPercent: number | null
  tipEntryMode: 'preset' | 'percent' | 'amount'
  customPercentInput: string
  autoGratEnabled: boolean
  autoGratEntryMode: AutoGratEntryMode
  autoGratPercentInput: string
  autoGratInput: string
  autoGratCents: number
  venue: string
  kind: VenueKind
  date: string
  scanning: boolean
  openingCamera: boolean
  scanError: string | null
  candidates: number[]
  previewUrl: string | null
  billCents: number
  tipCents: number
  totalCents: number
  onBillChange: (value: string) => void
  onTipPercent: (percent: number) => void
  onCustomPercentChange: (value: string) => void
  onAutoGratEnabled: (enabled: boolean) => void
  onAutoGratPercentChange: (value: string) => void
  onAutoGratPercentPreset: (percent: number) => void
  onAutoGratChange: (value: string) => void
  onTipChange: (value: string) => void
  onVenue: (value: string) => void
  onKind: (kind: VenueKind) => void
  onDate: (value: string) => void
  onScan: () => void
  onPickCandidate: (cents: number) => void
  onSave: () => void
}

export function TipCalculator({
  billInput,
  tipInput,
  tipPercent,
  presetPercent,
  tipEntryMode,
  customPercentInput,
  autoGratEnabled,
  autoGratEntryMode,
  autoGratPercentInput,
  autoGratInput,
  autoGratCents,
  venue,
  kind,
  date,
  scanning,
  openingCamera,
  scanError,
  candidates,
  previewUrl,
  billCents,
  tipCents,
  totalCents,
  onBillChange,
  onTipPercent,
  onCustomPercentChange,
  onAutoGratEnabled,
  onAutoGratPercentChange,
  onAutoGratPercentPreset,
  onAutoGratChange,
  onTipChange,
  onVenue,
  onKind,
  onDate,
  onScan,
  onPickCandidate,
  onSave,
}: Props) {
  const canSave = billCents > 0 && !scanning && !openingCamera
  const showAutoGratLine = autoGratEnabled || autoGratCents > 0

  return (
    <section className="card space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Tip calculator</p>
          <h2 className="mt-1 text-xl font-bold">What did it cost?</h2>
        </div>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Scanned bill"
            className="h-14 w-14 rounded-xl object-cover border border-line"
          />
        ) : null}
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-foam-muted">Bill before tip</span>
        <MoneyInput
          size="lg"
          value={billInput}
          onChange={onBillChange}
          aria-label="Bill amount in dollars"
        />
        <p className="text-[11px] leading-snug text-foam-faint">
          If the check shows auto-grat or a service charge, enter food/drink subtotal here — then
          match the % or dollar line below (often 18% or 20% for large parties).
        </p>
      </label>

      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line bg-bar px-3 py-3">
        <input
          type="checkbox"
          className="mt-1 size-4 shrink-0 accent-whiskey"
          checked={autoGratEnabled}
          onChange={(event) => onAutoGratEnabled(event.target.checked)}
        />
        <span className="space-y-1">
          <span className="block text-sm font-semibold text-foam">
            Auto-grat / service charge already on this bill
          </span>
          <span className="block text-[11px] text-foam-faint">
            Tip % applies only to the bill above — not the house-mandated line.
          </span>
        </span>
      </label>

      {autoGratEnabled ? (
        <div className="space-y-3">
          <div>
            <p className="mb-2 text-xs font-semibold text-foam-muted">Auto-grat on the check</p>
            <div className="flex flex-wrap gap-2">
              {[18, 20].map((percent) => {
                const active =
                  autoGratEntryMode === 'percent' && autoGratPercentInput === String(percent)
                return (
                  <button
                    key={percent}
                    type="button"
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      active
                        ? 'border-whiskey bg-whiskey/15 text-whiskey-light'
                        : 'border-line text-foam-muted'
                    }`}
                    onClick={() => onAutoGratPercentPreset(percent)}
                  >
                    {percent}%
                  </button>
                )
              })}
            </div>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-foam-muted">Auto-grat % (what the check says)</span>
            <div
              className={`flex min-h-12 items-center rounded-xl border bg-bar px-3 ${
                autoGratEntryMode === 'percent'
                  ? 'border-whiskey ring-1 ring-whiskey/40'
                  : 'border-line'
              }`}
            >
              <input
                className="min-w-0 flex-1 border-0 bg-transparent py-3 text-base font-bold text-foam tabular-nums outline-none"
                inputMode="decimal"
                placeholder="18"
                value={autoGratPercentInput}
                onChange={(event) => onAutoGratPercentChange(event.target.value)}
                aria-label="Auto-grat percent"
              />
              <span className="shrink-0 pl-1 font-bold text-foam-faint">%</span>
            </div>
            {autoGratEntryMode === 'percent' && billCents > 0 && autoGratCents > 0 ? (
              <p className="text-[11px] text-foam-faint">
                = {formatMoney(autoGratCents)} on this bill
              </p>
            ) : null}
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-foam-muted">Or exact auto-grat / service charge $</span>
            <div
              className={
                autoGratEntryMode === 'amount' ? 'rounded-xl ring-1 ring-whiskey/40' : undefined
              }
            >
              <MoneyInput
                size="md"
                value={autoGratInput}
                onChange={onAutoGratChange}
                aria-label="Auto-grat amount in dollars"
              />
            </div>
          </label>
        </div>
      ) : null}

      {candidates.length > 1 ? (
        <div className="space-y-1">
          <p className="text-[11px] text-foam-faint">Wrong total? Pick another amount from the receipt:</p>
          <div className="flex flex-wrap gap-2">
            {candidates.map((cents) => (
              <button
                key={cents}
                type="button"
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  cents === billCents
                    ? 'border-whiskey bg-whiskey/15 text-whiskey-light'
                    : 'border-line text-foam-muted'
                }`}
                onClick={() => onPickCandidate(cents)}
              >
                {formatMoney(cents)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {scanError ? <p className="text-sm text-danger">{scanError}</p> : null}

      <button type="button" className="secondary" onClick={onScan} disabled={scanning || openingCamera}>
        {openingCamera ? 'Opening camera…' : scanning ? 'Reading the receipt…' : 'Scan bill'}
      </button>
      <p className="text-xs text-foam-faint">
        Defaults to {DEFAULT_TIP_PERCENT}% after a scan. If the total includes auto-grat, lower the
        bill field and fill auto-grat below — OCR only sees numbers, not etiquette.
      </p>

      <div>
        <p className="mb-2 text-xs font-semibold text-foam-muted">Tip</p>
        <div className="grid grid-cols-4 gap-2">
          {TIP_PERCENTS.map((percent) => {
            const active = percent === presetPercent
            return (
              <button
                key={percent}
                type="button"
                className={`min-h-12 rounded-xl border text-sm font-bold ${
                  active
                    ? 'border-whiskey bg-whiskey text-bar'
                    : 'border-line bg-bar-overlay text-foam'
                }`}
                onClick={() => onTipPercent(percent)}
              >
                {percent}%
              </button>
            )
          })}
        </div>
        <label className="mt-2 block space-y-1">
          <span className="text-xs font-semibold text-foam-muted">Other tip %</span>
          <div
            className={`flex min-h-12 items-center rounded-xl border bg-bar-overlay px-3 ${
              tipEntryMode === 'percent'
                ? 'border-whiskey ring-1 ring-whiskey/40'
                : 'border-line'
            }`}
          >
            <input
              className="min-w-0 flex-1 border-0 bg-transparent py-3 text-base font-bold text-foam tabular-nums outline-none"
              inputMode="decimal"
              placeholder="e.g. 22"
              value={customPercentInput}
              onChange={(event) => onCustomPercentChange(event.target.value)}
              aria-label="Custom tip percent"
            />
            <span className="shrink-0 pl-1 font-bold text-foam-faint">%</span>
          </div>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-foam-muted">
          Tip amount — or type dollars if you left a flat amount
        </span>
        <MoneyInput
          size="md"
          value={tipInput}
          onChange={onTipChange}
          aria-label="Tip amount in dollars"
        />
      </label>

      <div
        className={`grid gap-3 rounded-xl bg-bar px-3 py-3 ${showAutoGratLine ? 'grid-cols-3' : 'grid-cols-2'}`}
      >
        {showAutoGratLine ? (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-foam-faint">Auto-grat</p>
            <p className="text-lg font-bold text-foam-muted">{formatMoney(autoGratCents)}</p>
          </div>
        ) : null}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-foam-faint">Your tip</p>
          <p className="text-xl font-bold text-whiskey-light">{formatMoney(tipCents)}</p>
        </div>
        <div className={showAutoGratLine ? '' : 'text-right'}>
          <p className="text-[11px] uppercase tracking-wide text-foam-faint">You pay</p>
          <p className="text-xl font-bold">{formatMoney(totalCents)}</p>
        </div>
      </div>
      <p className="text-xs text-foam-faint">
        {autoGratCents > 0 || autoGratEnabled
          ? `${formatPercent(tipPercent)} on ${formatMoney(billCents)} before auto-grat${
              autoGratCents > 0 ? ` · ${formatMoney(autoGratCents)} already on the check` : ''
            }.`
          : `${formatPercent(tipPercent)} on the bill before tip.`}
      </p>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-foam-muted">Place</span>
        <input
          className="field"
          placeholder="Bar name, restaurant, whatever"
          value={venue}
          onChange={(event) => onVenue(event.target.value)}
        />
      </label>

      <div className="grid grid-cols-3 gap-2">
        {VENUE_KINDS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`min-h-10 rounded-xl border text-sm font-semibold ${
              kind === option.id
                ? 'border-whiskey/70 bg-whiskey/15 text-whiskey-light'
                : 'border-line text-foam-muted'
            }`}
            onClick={() => onKind(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-foam-muted">Date</span>
        <input
          className="field"
          type="date"
          value={date}
          onChange={(event) => onDate(event.target.value)}
        />
      </label>

      <button type="button" className="primary" disabled={!canSave} onClick={onSave}>
        Save tab
      </button>
    </section>
  )
}
